import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Shield, AlertTriangle, Activity, Download, Server,
  Wifi, Eye, AlertCircle, Network, Cpu, Car, Camera,
  Users, Radio, Link, MessageSquare, Phone, Gauge,
  Webhook, BarChart3, Globe
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../utils/authFetch';
import SEO from '../components/SEO';

// Dashboard sub-components
import OverviewTab from '../components/dashboard/OverviewTab';
import AddDeviceModal from '../components/dashboard/AddDeviceModal';

// Phase 3 Components
import IoT5GSecurityDashboard from '../components/IoT5GSecurityDashboard';
import SurveillanceIoTDashboard from '../components/SurveillanceIoTDashboard';
import TeamManagement from '../components/TeamManagement';
import URLScanner from '../components/URLScanner';
import SMSAnalyzer from '../components/SMSAnalyzer';
import DeviceRegistry from '../components/DeviceRegistry';
import IoTSecurity from '../components/IoTSecurity';
import GSMFraudDetection from '../components/GSMFraudDetection';
import AdvancedAutomotiveSecurity from '../components/AdvancedAutomotiveSecurity';
import AnalyticsReports from '../components/AnalyticsReports';
import TelecomIntegration from '../components/TelecomIntegration';
import CSPOperationsDashboard from '../components/CSPOperationsDashboard';
import WebhookManager from '../components/WebhookManager';
import DownloadCenter from '../components/DownloadCenter';
import MobileProtectionDashboard from '../components/MobileProtectionDashboard';
import PricingPlans from '../components/PricingPlans';
import NetFlowAnalytics from '../components/NetFlowAnalytics';
import MultiChannelAlerts from '../components/MultiChannelAlerts';
import SecurityIntelligence from '../components/SecurityIntelligence';
import AISecurityDashboard from '../components/AISecurityDashboard';

const SecureSphere = () => {
  const { user, canAccessTab, permissions } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [devices, setDevices] = useState([]);
  const [threats, setThreats] = useState([]);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [addingDevice, setAddingDevice] = useState(false);
  const [newDevice, setNewDevice] = useState({ name: '', type: 'server', ip: '', os: '', segment: 'enterprise' });

  const fetchDevices = useCallback(async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/devices`);
      if (res.ok) { const data = await res.json(); setDevices(data); }
    } catch (error) { console.error('Device fetch failed:', error.message); }
  }, []);

  const fetchThreats = useCallback(async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/threats`);
      if (res.ok) { const data = await res.json(); setThreats(data); }
    } catch (error) { console.error('Threats fetch failed:', error.message); }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchThreats();
  }, [fetchDevices, fetchThreats]);

  const handleAddDevice = async () => {
    if (!newDevice.name || !newDevice.ip) { alert('Please fill in device name and IP address'); return; }
    setAddingDevice(true);
    try {
      await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/security/devices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newDevice.name, device_type: newDevice.type, ip_address: newDevice.ip, os: newDevice.os || 'Unknown', segment: newDevice.segment })
      });
      setDevices(prev => [...prev, { id: Date.now(), name: newDevice.name, type: newDevice.type, ip: newDevice.ip, os: newDevice.os || 'Unknown', status: 'active', threats: 0, lastSeen: 'Just now' }]);
      setShowAddDeviceModal(false);
      setNewDevice({ name: '', type: 'server', ip: '', os: '', segment: 'enterprise' });
    } catch (error) {
      setDevices(prev => [...prev, { id: Date.now(), name: newDevice.name, type: newDevice.type, ip: newDevice.ip, os: newDevice.os || 'Unknown', status: 'active', threats: 0, lastSeen: 'Just now' }]);
      setShowAddDeviceModal(false);
      setNewDevice({ name: '', type: 'server', ip: '', os: '', segment: 'enterprise' });
    } finally { setAddingDevice(false); }
  };

  const tabs = [
    { value: 'overview', label: 'Overview', icon: Shield },
    { value: 'url-scanner', label: 'URL Scanner', icon: Link },
    { value: 'sms-analyzer', label: 'SMS Analysis', icon: MessageSquare },
    { value: 'mobile-protection', label: 'Mobile', icon: Phone },
    { value: 'devices', label: 'Devices', icon: Network },
    { value: 'iot', label: 'IoT', icon: Wifi },
    { value: 'surveillance', label: 'Surveillance', icon: Camera },
    { value: '5g-iot', label: '5G-IoT', icon: Radio },
    { value: 'gsm-fraud', label: 'GSM Fraud', icon: AlertTriangle },
    { value: 'automotive', label: 'Automotive', icon: Car },
    { value: 'analytics', label: 'Analytics', icon: BarChart3 },
    { value: 'telecom', label: 'Telecom', icon: Globe },
    { value: 'csp-ops', label: 'CSP Ops', icon: Server },
    { value: 'webhooks', label: 'Webhooks', icon: Webhook },
    { value: 'agents', label: 'Agents', icon: Download },
    { value: 'pricing', label: 'Pricing', icon: Gauge },
    { value: 'team', label: 'Team', icon: Users },
    { value: 'netflow', label: 'NetFlow', icon: Activity, testId: 'tab-netflow' },
    { value: 'alerts', label: 'Alerts', icon: AlertCircle, testId: 'tab-alerts' },
    { value: 'intelligence', label: 'Intelligence', icon: Eye, testId: 'tab-intelligence' },
    { value: 'ai-security', label: 'AI Security', icon: Cpu, testId: 'tab-ai-security' },
  ];

  const visibleTabs = tabs.filter(t => canAccessTab(t.value));

  return (
    <DashboardLayout title="SecureSphere - Enterprise Security">
      <SEO
        title="SecureSphere - Telco-Grade Enterprise Security | Infuse-ai"
        description="Enterprise-grade network security, IoT protection, and AI workload security with real-time threat detection"
        keywords="enterprise security, network security, IoT security, AI security, threat detection, telco-grade security"
      />

      {permissions && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-xs px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 font-medium">
            {permissions.role_label}
          </span>
          <span className="text-xs text-neutral-500">{permissions.accessible_tabs.length} tabs accessible</span>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="overflow-x-auto pb-2">
          <TabsList className="inline-flex w-auto min-w-full gap-1">
            {visibleTabs.map(({ value, label, icon: Icon, testId }) => (
              <TabsTrigger key={value} value={value} data-testid={testId} className="flex items-center gap-1">
                <Icon className="w-3 h-3" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview">
          <OverviewTab threats={threats} devices={devices} setActiveTab={setActiveTab} />
        </TabsContent>
        <TabsContent value="url-scanner"><URLScanner /></TabsContent>
        <TabsContent value="sms-analyzer"><SMSAnalyzer /></TabsContent>
        <TabsContent value="mobile-protection"><MobileProtectionDashboard /></TabsContent>
        <TabsContent value="devices"><DeviceRegistry /></TabsContent>
        <TabsContent value="iot"><IoTSecurity /></TabsContent>
        <TabsContent value="surveillance"><SurveillanceIoTDashboard /></TabsContent>
        <TabsContent value="5g-iot"><IoT5GSecurityDashboard /></TabsContent>
        <TabsContent value="gsm-fraud"><GSMFraudDetection /></TabsContent>
        <TabsContent value="automotive"><AdvancedAutomotiveSecurity /></TabsContent>
        <TabsContent value="analytics"><AnalyticsReports /></TabsContent>
        <TabsContent value="telecom"><TelecomIntegration /></TabsContent>
        <TabsContent value="csp-ops"><CSPOperationsDashboard /></TabsContent>
        <TabsContent value="webhooks"><WebhookManager /></TabsContent>
        <TabsContent value="agents"><DownloadCenter /></TabsContent>
        <TabsContent value="pricing"><PricingPlans onSelectPlan={() => {}} /></TabsContent>
        <TabsContent value="team"><TeamManagement platform="securesphere" /></TabsContent>
        <TabsContent value="netflow"><NetFlowAnalytics /></TabsContent>
        <TabsContent value="alerts"><MultiChannelAlerts /></TabsContent>
        <TabsContent value="intelligence"><SecurityIntelligence /></TabsContent>
        <TabsContent value="ai-security"><AISecurityDashboard /></TabsContent>
      </Tabs>

      {showAddDeviceModal && (
        <AddDeviceModal
          newDevice={newDevice}
          setNewDevice={setNewDevice}
          handleAddDevice={handleAddDevice}
          addingDevice={addingDevice}
          onClose={() => setShowAddDeviceModal(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default SecureSphere;
